import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    FormFeedback
} from 'reactstrap';
import Select from 'react-select';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../../lib/supabase';
import './ContactPage.scss';

const ContactUsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [contactData, setContactData] = useState(null);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        website: '',
        howDidYouHear: null,
        message: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    // FIXED: Scroll to top on page load and route change
    useEffect(() => {
        // Multiple scroll methods to ensure it works
        const scrollToTop = () => {
            window.scrollTo(0, 0);
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        };

        // Call immediately
        scrollToTop();

        // Call after a short delay to ensure DOM is fully rendered
        const timer1 = setTimeout(scrollToTop, 0);
        const timer2 = setTimeout(scrollToTop, 50);
        const timer3 = setTimeout(scrollToTop, 100);

        // Disable browser's default scroll restoration
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [location.pathname]); // Re-run when route changes

    // How did you hear options
    const howDidYouHearOptions = [
        { value: 'search', label: 'Search Engine (Google, Bing, etc.)' },
        { value: 'social', label: 'Social Media' },
        { value: 'friend', label: 'Friend or Colleague' },
        { value: 'advertisement', label: 'Advertisement' },
        { value: 'event', label: 'Event or Conference' },
        { value: 'podcast', label: 'Podcast' },
        { value: 'other', label: 'Other' }
    ];

    // Set contact info directly
    useEffect(() => {
        setContactData({
            company_name: 'Quantumcrest Solutions Ltd',
            trading_name: 'Reachtix',
            registration: 'Registered in England and Wales',
            company_number: '16088299',
            registered_office: '400 Thames Valley Park Drive, Reading, RG6 1PT',
            dun_number: '232675067',
            phone: '+44 118 228 7381',
            email: 'info@reachtix.com',
            hours: 'Mon-Fri: 9.00 AM – 5.00 PM'
        });
        setLoading(false);
    }, []);

    const showAlert = (message, type = 'info') => {
        setAlert({ visible: true, message, type });
        setTimeout(() => setAlert({ visible: false, message: '', type: '' }), 5000);
    };

    // Validation rules
    const validateForm = () => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.name.trim()) errors.name = 'Name is required';
        else if (formData.name.length < 2) errors.name = 'Name must be at least 2 characters';

        if (!formData.email.trim()) errors.email = 'Email is required';
        else if (!emailRegex.test(formData.email)) errors.email = 'Please enter a valid email address';

        if (!formData.howDidYouHear) errors.howDidYouHear = 'Please select how you heard about us';

        if (!formData.message.trim()) errors.message = 'Message is required';
        else if (formData.message.length < 10) errors.message = 'Message must be at least 10 characters';

        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            howDidYouHear: selectedOption
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        const errors = validateForm();
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            showAlert('Please fix the errors in the form', 'danger');
            return;
        }

        setSubmitting(true);

        try {
            const response = await fetch(`${supabase.supabaseUrl}/functions/v1/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabase.supabaseKey}`
                },
                body: JSON.stringify({
                    firstName: formData.name,
                    lastName: '',
                    email: formData.email,
                    company: formData.company,
                    website: formData.website,
                    heardAbout: formData.howDidYouHear?.value,
                    howDidYouHearLabel: formData.howDidYouHear?.label,
                    message: formData.message,
                    timestamp: new Date().toISOString(),
                    source: 'contact_page'
                })
            });

            const result = await response.json();

            if (!response.ok) {
                // Handle specific error types from the response
                if (result.error === 'DUPLICATE_SUBMISSION') {
                    showAlert(result.message || 'You have already submitted a request. Please wait for our response before submitting another request.', 'warning');
                } else {
                    throw new Error(result.error || result.message || 'Failed to send message');
                }
                return;
            }

            const { error: dbError } = await supabase
                .from('contact_form_submissions')
                .insert([{
                    first_name: formData.name,
                    last_name: "",
                    email: formData.email,
                    company: formData.company,
                    website: formData.website,
                    heard_about: formData.howDidYouHear?.value,
                    message: formData.message,
                    status: 'pending'
                }]);

            if (dbError) {
                console.error('Database backup failed:', dbError);
            }

            showAlert('✓ Thank you for your message! We\'ll get back to you within 24 hours.', 'success');

            setFormData({
                name: '',
                email: '',
                company: '',
                website: '',
                howDidYouHear: null,
                message: ''
            });
            setFormErrors({});
            setSubmitted(false);

        } catch (error) {
            console.error('Error submitting form:', error);
            showAlert(error.message || 'Failed to send message. Please try again or email us directly.', 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    // SVG Pattern for top decoration
    const TopPattern = () => (
        <svg className="top-pattern" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="rgba(0, 123, 255, 0.05)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
            <path fill="rgba(0, 123, 255, 0.03)" d="M0,160L48,176C96,192,192,224,288,224C384,224,480,192,576,176C672,160,768,160,864,176C960,192,1056,224,1152,224C1248,224,1344,192,1392,176L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="contact-page">
            <TopPattern />

            {alert.visible && (
                <Alert color={alert.type} className="m-3" fade={false}>
                    {alert.message}
                </Alert>
            )}

            {/* Hero Section */}
            <section className="contact-hero">
                <Container>
                    <Row>
                        <Col lg={8}>
                            <h1>
                                Contact Us
                            </h1>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Contact Form Section */}
            <section className="contact-section">
                <Container>
                    <Row className="align-items-start">
                        {/* Left Side - Description and Contact Info */}
                        <Col lg={5} className="mb-5 mb-lg-0">
                            <div className="contact-description">
                                <h2>Let's Talk About Growing Your Business</h2>
                                <p>
                                    Tell us about your business and goals — we'll show you how to turn your marketing into real results.
                                </p>

                                <div className="contact-info-box">
                                    <p className="company-info">
                                        <strong>QUANTUMCREST SOLUTIONS LTD</strong><br />
                                        (trading as Reachtix)<br />
                                        Registered in England and Wales.<br />
                                        Company number: {contactData?.company_number}<br />
                                        Registered office: {contactData?.registered_office}<br />
                                        D-U-N-S©: {contactData?.dun_number}
                                    </p>

                                    <div className="contact-details">
                                        <div className="detail-item">
                                            <span className="detail-label">📞 Quick Contact</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Email:</span>
                                            <span className="detail-value">{contactData?.email}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Hours:</span>
                                            <span className="detail-value">{contactData?.hours}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Phone:</span>
                                            <span className="detail-value">{contactData?.phone}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Right Side - Form */}
                        <Col lg={7}>
                            <Card className="contact-card">
                                <CardBody>
                                    <Form onSubmit={handleSubmit} noValidate>
                                        <FormGroup>
                                            <Label for="name">Name *</Label>
                                            <Input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Your name"
                                                disabled={submitting}
                                                invalid={submitted && !!formErrors.name}
                                            />
                                            {submitted && formErrors.name && (
                                                <FormFeedback>{formErrors.name}</FormFeedback>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label for="email">Email *</Label>
                                            <Input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Your email address"
                                                disabled={submitting}
                                                invalid={submitted && !!formErrors.email}
                                            />
                                            {submitted && formErrors.email && (
                                                <FormFeedback>{formErrors.email}</FormFeedback>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label for="company">Company (optional)</Label>
                                            <Input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                placeholder="Your company name"
                                                disabled={submitting}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label for="website">Website (optional)</Label>
                                            <Input
                                                type="url"
                                                id="website"
                                                name="website"
                                                value={formData.website}
                                                onChange={handleInputChange}
                                                placeholder="https://yourcompany.com"
                                                disabled={submitting}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label for="howDidYouHear">How did you hear about us?</Label>
                                            <Select
                                                id="howDidYouHear"
                                                name="howDidYouHear"
                                                options={howDidYouHearOptions}
                                                value={formData.howDidYouHear}
                                                onChange={handleSelectChange}
                                                placeholder="Select an option"
                                                isDisabled={submitting}
                                                className={submitted && formErrors.howDidYouHear ? 'is-invalid-select' : ''}
                                            />
                                            {submitted && formErrors.howDidYouHear && (
                                                <div className="invalid-feedback d-block">{formErrors.howDidYouHear}</div>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label for="message">Message *</Label>
                                            <Input
                                                type="textarea"
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                rows="5"
                                                placeholder="Tell us about your business and goals..."
                                                disabled={submitting}
                                                invalid={submitted && !!formErrors.message}
                                            />
                                            {submitted && formErrors.message && (
                                                <FormFeedback>{formErrors.message}</FormFeedback>
                                            )}
                                        </FormGroup>

                                        <Button
                                            type="submit"
                                            color="primary"
                                            disabled={submitting}
                                            block
                                            style={{ width: '100%' }}
                                        >
                                            {submitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Sending...
                                                </>
                                            ) : 'Send Message'}
                                        </Button>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ContactUsPage;